/* Copy da gestão de releases (ADR-0659/0702/0704/0809): header, alerta explicativo do fluxo
   R2→registro→promoção, abas, toolbar de filtros inline e os toasts de cada ação (individual e em
   lote). `PLATFORM_LABELS` (macOS/Windows/Linux) fica fora daqui — são nomes próprios, idênticos
   nos dois idiomas. */
import type { ReleaseStatus } from '@planuze/admin-contracts';
import type { Bilingual } from '../bilingual.js';
import { useBilingual } from '../locale-provider.js';

interface ReleasesContent {
  readonly pageTitle: string;
  readonly pageDescription: string;
  readonly registerButton: string;
  /* O alerta info mistura texto simples e trechos em <strong>; cada segmento vira um campo para
     que a marcação (JSX) fique no componente e só o texto venha da copy. */
  readonly infoIntro: string;
  readonly infoOnlyR2Strong: string;
  readonly infoAfterOnlyR2: string;
  readonly infoPromoteStrong: string;
  readonly infoAfterPromote: string;
  readonly infoMandatoryStrong: string;
  readonly infoAfterMandatory: string;
  readonly releasesTab: string;
  readonly bucketTab: string;
  readonly searchAriaLabel: string;
  readonly searchPlaceholder: string;
  readonly channelFilterAriaLabel: string;
  readonly statusFilterAriaLabel: string;
  readonly mandatoryFilterAriaLabel: string;
  readonly platformFacetLabel: string;
  /** "Todos" — usado nos filtros de canal e status (concordância masculina). */
  readonly allLabel: string;
  readonly statusLabels: Readonly<Record<ReleaseStatus, string>>;
  /** "Todas" — filtro de obrigatoriedade (concordância feminina: "todas as versões"). */
  readonly mandatoryAllLabel: string;
  readonly mandatoryOnlyLabel: string;
  readonly optionalLabel: string;
  readonly loadError: string;
  readonly loadMore: string;
  readonly toastPromotedMandatory: string;
  readonly toastPromoted: string;
  readonly toastTakenDown: string;
  readonly toastMandatoryUpdated: string;
  readonly toastOptionalUpdated: string;
  readonly toastDeletedFromR2: string;
  readonly toastRecordOnlyRemoved: string;
  readonly toastVersionPurgedFromR2: (objectsDeleted: number, bytesDeleted: number) => string;
  /** "Ação aplicada a N versão(ões)." — resultado de lote sem falhas. */
  readonly toastBulkApplied: (count: number) => string;
  /** "N ok, M recusada(s). <detalhe>" — resultado de lote parcialmente aplicado. */
  readonly toastBulkPartial: (okCount: number, failedCount: number, detail: string) => string;
}

const RELEASES_CONTENT: Bilingual<ReleasesContent> = {
  'pt-BR': {
    pageTitle: 'Atualizações do app',
    pageDescription:
      'Versões publicadas no R2 — registre, promova, marque obrigatória ou tire do ar.',
    registerButton: 'Registrar release',
    infoIntro: 'Os binários ficam no R2. ',
    infoOnlyR2Strong: 'Só existir no R2 não libera',
    infoAfterOnlyR2: ' — registre e ',
    infoPromoteStrong: 'promova',
    infoAfterPromote: ' aqui para disponibilizar aos usuários. Marcar ',
    infoMandatoryStrong: 'obrigatória',
    infoAfterMandatory:
      ' bloqueia o uso de versões antigas até atualizar. Tirar do ar remove do feed.',
    releasesTab: 'Releases',
    bucketTab: 'Bucket R2',
    searchAriaLabel: 'Buscar releases',
    searchPlaceholder: 'Buscar por versão…',
    channelFilterAriaLabel: 'Filtrar por canal',
    statusFilterAriaLabel: 'Filtrar por status',
    mandatoryFilterAriaLabel: 'Filtrar por obrigatoriedade',
    platformFacetLabel: 'Plataforma',
    allLabel: 'Todos',
    statusLabels: { draft: 'Draft', promoted: 'Promovida', yanked: 'Removida' },
    mandatoryAllLabel: 'Todas',
    mandatoryOnlyLabel: 'Obrigatórias',
    optionalLabel: 'Opcionais',
    loadError: 'Falha ao carregar as releases.',
    loadMore: 'Carregar mais',
    toastPromotedMandatory: 'Promovida como OBRIGATÓRIA.',
    toastPromoted: 'Release promovida.',
    toastTakenDown: 'Release tirada do ar.',
    toastMandatoryUpdated: 'Versão marcada como obrigatória em todos os artefatos.',
    toastOptionalUpdated: 'Versão marcada como opcional em todos os artefatos.',
    toastDeletedFromR2: 'Versão apagada do R2 e do índice.',
    toastRecordOnlyRemoved:
      'Registro removido do índice. O binário publicado permanece imutável no R2/GitHub e pode ser redescoberto.',
    toastVersionPurgedFromR2: (objectsDeleted, bytesDeleted) =>
      `${objectsDeleted} objeto(s) excluído(s) do R2; ${new Intl.NumberFormat('pt-BR').format(bytesDeleted)} bytes liberados. Histórico e métricas preservados.`,
    toastBulkApplied: (count) => `Ação aplicada a ${count} versão(ões).`,
    toastBulkPartial: (okCount, failedCount, detail) =>
      `${okCount} ok, ${failedCount} recusada(s). ${detail}`,
  },
  'en-US': {
    pageTitle: 'App updates',
    pageDescription: 'Versions published to R2 — register, promote, mark mandatory, or take down.',
    registerButton: 'Register release',
    infoIntro: 'Binaries live in R2. ',
    infoOnlyR2Strong: 'Existing in R2 alone does not release it',
    infoAfterOnlyR2: ' — register and ',
    infoPromoteStrong: 'promote',
    infoAfterPromote: ' it here to make it available to users. Marking it ',
    infoMandatoryStrong: 'mandatory',
    infoAfterMandatory:
      ' blocks the use of older versions until updated. Taking down removes it from the feed.',
    releasesTab: 'Releases',
    bucketTab: 'R2 bucket',
    searchAriaLabel: 'Search releases',
    searchPlaceholder: 'Search by version…',
    channelFilterAriaLabel: 'Filter by channel',
    statusFilterAriaLabel: 'Filter by status',
    mandatoryFilterAriaLabel: 'Filter by mandatory status',
    platformFacetLabel: 'Platform',
    allLabel: 'All',
    statusLabels: { draft: 'Draft', promoted: 'Promoted', yanked: 'Taken down' },
    mandatoryAllLabel: 'All',
    mandatoryOnlyLabel: 'Mandatory',
    optionalLabel: 'Optional',
    loadError: 'Failed to load releases.',
    loadMore: 'Load more',
    toastPromotedMandatory: 'Promoted as MANDATORY.',
    toastPromoted: 'Release promoted.',
    toastTakenDown: 'Release taken down.',
    toastMandatoryUpdated: 'Version marked mandatory across all artifacts.',
    toastOptionalUpdated: 'Version marked optional across all artifacts.',
    toastDeletedFromR2: 'Version deleted from R2 and the index.',
    toastRecordOnlyRemoved:
      'Record removed from the index. The published binary remains immutable in R2/GitHub and can be rediscovered.',
    toastVersionPurgedFromR2: (objectsDeleted, bytesDeleted) =>
      `${objectsDeleted} object(s) deleted from R2; ${new Intl.NumberFormat('en-US').format(bytesDeleted)} bytes freed. History and metrics preserved.`,
    toastBulkApplied: (count) => `Action applied to ${count} version(s).`,
    toastBulkPartial: (okCount, failedCount, detail) =>
      `${okCount} ok, ${failedCount} failed. ${detail}`,
  },
};

export const useReleasesContent = (): ReleasesContent => useBilingual(RELEASES_CONTENT);
